// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React from 'react';
import {
    Select, MenuItem, Checkbox, ListItemText, OutlinedInput, 
    InputLabel, FormControl, SelectChangeEvent, Divider
} from '@mui/material';
import { RiskFilterKey } from '../data/utils/types';

const riskOptions: { key: RiskFilterKey, label: string }[] = [
    { key: "hasNoGoLive", label: "Has Gone Live" },
    { key: "noSupportCases6mo", label: "Support Activity (Last 6 Mo)" },
    { key: "hasEolProduct", label: "Using EOL Products" },
    { key: "hasAbandonedMigrations", label: "Abandoned Migrations" },
    { key: "hasMigrationDelays", label: "Migration Delays" },
    { key: "hasRecentEscalations", label: "Escalations (Last 3 Mo)" }
];

interface RiskIndicatorSelectProps {
    selectedRisks: RiskFilterKey[];
    onRiskChange: (newRisks: RiskFilterKey[]) => void;
}

export const RiskIndicatorSelect: React.FC<RiskIndicatorSelectProps> = ({ selectedRisks, onRiskChange }) => {

    const handleChange = (event: SelectChangeEvent<typeof selectedRisks>) => {
        const { target: { value } } = event;
        const valueArray = typeof value === 'string' ? value.split(',') : value;

        if (valueArray.includes('clear-all' as RiskFilterKey)){
            onRiskChange([]);
            return;
        }
        onRiskChange(valueArray as RiskFilterKey[]);
    };

    return (
        <FormControl sx={{ m: 1, width: 300 }} size="small">
            <InputLabel>Risk Indicator</InputLabel>
            <Select
                multiple
                value={selectedRisks}
                onChange={handleChange}
                input={<OutlinedInput label="Risk Indicator" />}
                renderValue={(selected) => {
                    if (selected.length === 0) return "All Indicators";
                    if (selected.length === riskOptions.length) return "All Indicators";
                    return `${selected.length} Selected`;
                }}
            >   
                {selectedRisks.length > 0 && (
                    <MenuItem 
                        value={'clear-all' as RiskFilterKey} 
                        sx={{ 
                            fontWeight: 'bold',
                            justifyContent: 'center'  
                        }}
                    >
                        <ListItemText primary="Clear All" 
                        primaryTypographyProps={{ align: 'center' }}/>
                    </MenuItem>
                )}

                {selectedRisks.length > 0 && <Divider />}

                {/* Render Options */}
                {riskOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                        <Checkbox checked={selectedRisks.indexOf(option.key) > -1} />
                        <ListItemText primary={option.label} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};
